import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import profesorParticularApiService from '../services/ProfesorParticularApiService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import { useAuth } from '../contexts/AuthContext';
import { isAdminColegio } from '../utils/adminMenu';
import ParticularCuentaFields, {
  EMPTY_PARTICULAR_FORM,
  buildParticularPayload,
  formatCuit,
  mapParticularToForm,
  validateParticularForm
} from './common/ParticularCuentaFields';

const getValue = (source, keys, fallback = '') => {
  if (!source) return fallback;
  const value = keys.map((key) => source[key]).find((item) => item !== undefined && item !== null);
  return value ?? fallback;
};

const AdminEditProfesorParticular = () => {
  const navigate = useNavigate();
  const { particularId } = useParams();
  const { user, logout } = useAuth();
  const isGeneralAdmin = !isAdminColegio(user);

  const [form, setForm] = useState({ ...EMPTY_PARTICULAR_FORM, activo: true });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    if (!isGeneralAdmin) {
      navigate('/admin/select-colegio', { replace: true });
    }
  }, [isGeneralAdmin, navigate]);

  useEffect(() => {
    const loadParticular = async () => {
      if (!particularId) return;
      setIsLoading(true);
      setMessage('');
      try {
        const response = await profesorParticularApiService.getProfesorParticularById(particularId);
        const reg = response?.profesorParticular || response?.ProfesorParticular || response;
        setForm({
          ...mapParticularToForm(reg),
          activo: Boolean(getValue(reg, ['activo', 'Activo'], true))
        });
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar el particular.');
        setMessageType('error');
      } finally {
        setIsLoading(false);
      }
    };

    loadParticular();
  }, [particularId]);

  const handleChange = (field, value) => {
    const nextValue = field === 'cuit' ? formatCuit(value) : value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateParticularForm(form, { dniRequired: false });
    if (validationError) {
      setMessage(validationError);
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Guardando cambios...');
    setMessageType('info');

    try {
      await profesorParticularApiService.updateProfesorParticular(particularId, {
        ...buildParticularPayload(form),
        activo: form.activo
      });
      setMessage('Cuenta actualizada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin/select-colegio', { replace: true, state: { scope: 'particulares' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar el particular.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-standalone">
      <header className="admin-standalone-header">
        <div>
          <p className="eyebrow">Administración general</p>
          <h1>Editar particular</h1>
          <p className="subtitle">Actualiza la institución y el administrador de la cuenta.</p>
        </div>
        <div className="admin-standalone-actions">
          <ActiveColegioBadge />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/select-colegio', { state: { scope: 'particulares' } })}
          >
            <i className="fas fa-arrow-left" />
            <span>Volver a particulares</span>
          </button>
          <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
        </div>
      </header>

      <div className="admin-dual-panel">
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-user-edit" />
              <span>Datos de la cuenta</span>
            </div>
          </div>
          <div className="card-content">
            {isLoading ? (
              <p className="state">Cargando particular...</p>
            ) : (
              <form onSubmit={handleSubmit} className="prof-create-form">
                <ParticularCuentaFields form={form} onChange={handleChange} dniRequired={false} />

                <div className="form-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.activo}
                      onChange={(e) => handleChange('activo', e.target.checked)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    Cuenta activa
                  </label>
                </div>

                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/select-colegio', { state: { scope: 'particulares' } })}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Guardar cambios</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEditProfesorParticular;
